/* =========================================================
   PH PROMOTIONS v2.0
   PH Perfumería — Motor central de promociones

   Modos soportados:
   - volume  -> 10% / 15% / 20%
   - 3x2     -> todos los perfumes individuales entran al 3×2
   - none    -> sin promoción visual en PDP

   Reglas 3×2:
   - Todos los perfumes individuales son elegibles.
   - Paquetes quedan excluidos.
   - Durante 3×2, los elegibles NO cuentan para volume.
   - Se cuentan unidades elegibles dentro del mismo pool.
   - El preview identifica las unidades elegibles más baratas
     como candidatas gratuitas.
   - Shopify sigue siendo la autoridad del descuento real.

   La campaña usa una fecha real de inicio y fin. Al vencer,
   el motor promocional se desactiva automáticamente.
   ========================================================= */

(() => {
  const THEME_SETTINGS = window.PHPromotionSettings || {};

  const PH_PROMOTIONS_CONFIG = {
    volume: {
      enabled: true,
      tiers: [
        { minimum: 5, discount: 20 },
        { minimum: 3, discount: 15 },
        { minimum: 2, discount: 10 }
      ]
    },

    threeForTwo: {
      enabled: true,
      groupSize: 3,
      payFor: 2,
      freeItemRule: 'lowest_price'
    }
  };

  class PHPromotions {
    constructor() {
      this.cart = null;

      this.selectedMode = this.normalizeMode(THEME_SETTINGS.selectedMode);
      this.threeForTwoStart = String(THEME_SETTINGS.threeForTwoStart || '').trim();
      this.threeForTwoEnd = String(THEME_SETTINGS.threeForTwoEnd || '').trim();

      this.effectiveMode = this.resolveEffectiveMode();
      this.engineMode = this.effectiveMode === 'none' ? 'none' : 'volume';

      this.state = this.getEmptyState();

      this.refreshTimer = null;
      this.quantityTimer = null;
      this.clockTimer = null;
    }

    getEmptyState() {
      return {
        selectedMode: this.selectedMode,
        effectiveMode: this.effectiveMode,
        engineMode: this.engineMode,
        mode: this.engineMode,
        campaignStatus: this.getCampaignStatus(),
        threeForTwoStart: this.threeForTwoStart,
        threeForTwoEnd: this.threeForTwoEnd,

        currentVariantType: 'regular',
        currentVariant3x2Eligible: false,
        currentProductType: 'perfume',

        actualQuantity: 0,
        selectedQuantity: 1,
        projectedQuantity: 1,
        visualQuantity: 1,

        tier: 0,
        discount: 0,
        actualTier: 0,
        selectedTier: 0,
        selectedDiscount: 0,
        priceDiscount: 0,

        nextTier: 2,
        nextDiscount: 10,
        remainingForNextTier: 1,

        threeForTwoActualQuantity: 0,
        threeForTwoSelectedQuantity: 0,
        threeForTwoProjectedQuantity: 0,
        threeForTwoActualFreeItems: 0,
        threeForTwoProjectedFreeItems: 0,
        threeForTwoRemainingForNext: 3,
        threeForTwoActualSavingsCents: 0,
        threeForTwoActualSavings: '$0.00',
        threeForTwoCheapestItem: null,
        threeForTwoFreeItems: []
      };
    }

    normalizeMode(mode) {
      const normalized = String(mode || 'volume').trim().toLowerCase();

      if (normalized === '3x2') return '3x2';
      if (normalized === 'none') return 'none';

      return 'volume';
    }

    parseDate(value) {
      if (!value) return null;

      const timestamp = Date.parse(value);
      return Number.isNaN(timestamp) ? null : timestamp;
    }

    getThreeForTwoDates() {
      return {
        start: this.parseDate(this.threeForTwoStart),
        end: this.parseDate(this.threeForTwoEnd)
      };
    }

    hasValidThreeForTwoDates() {
      const dates = this.getThreeForTwoDates();

      if (dates.start === null || dates.end === null) return false;
      if (dates.end <= dates.start) return false;

      return true;
    }

    getCampaignStatus() {
      if (this.selectedMode !== '3x2') return 'inactive';
      if (!this.hasValidThreeForTwoDates()) return 'invalid_dates';

      const dates = this.getThreeForTwoDates();
      const now = Date.now();

      if (now < dates.start) return 'scheduled';
      if (now > dates.end) return 'expired';

      return 'active';
    }

    resolveEffectiveMode() {
      if (this.selectedMode === 'none') return 'none';
      if (this.selectedMode === 'volume') return 'volume';

      if (this.selectedMode === '3x2') {
        if (!this.hasValidThreeForTwoDates()) {
          window.PH?.warn?.('Promotions: 3x2 seleccionado pero las fechas son inválidas.');
          return 'none';
        }

        const dates = this.getThreeForTwoDates();
        const now = Date.now();

        if (now >= dates.start && now <= dates.end) return '3x2';

        return 'none';
      }

      return 'volume';
    }

    refreshPromotionMode() {
      this.selectedMode = this.normalizeMode(THEME_SETTINGS.selectedMode);
      this.effectiveMode = this.resolveEffectiveMode();

      if (this.state) {
        this.state.selectedMode = this.selectedMode;
        this.state.effectiveMode = this.effectiveMode;
        this.state.campaignStatus = this.getCampaignStatus();
      }
    }

    init() {
      window.PHPromotions = this;

      this.logPromotionConfig();
      this.bindCartUpdates();
      this.bindQuantitySelector();
      this.bindVariantPromotionUpdates();
      this.bindPromotionClock();

      this.refresh();
    }

    logPromotionConfig() {
      window.PH?.log?.('Promotions — configuración:', {
        selectedMode: this.selectedMode,
        effectiveMode: this.effectiveMode,
        campaignStatus: this.getCampaignStatus(),
        start: this.threeForTwoStart,
        end: this.threeForTwoEnd
      });
    }

    bindPromotionClock() {
      if (this.clockTimer) window.clearInterval(this.clockTimer);

      this.clockTimer = window.setInterval(() => {
        const previousMode = this.effectiveMode;
        const previousStatus = this.getCampaignStatus();

        this.refreshPromotionMode();

        const currentStatus = this.getCampaignStatus();

        if (
          previousMode !== this.effectiveMode ||
          previousStatus !== currentStatus
        ) {
          window.PH?.log?.('Promotions — cambio automático:', {
            effectiveMode: this.effectiveMode,
            campaignStatus: currentStatus
          });

          this.renderCurrentState();
        }
      }, 60000);
    }

    async getCart() {
      try {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${root}cart.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        return await response.json();
      } catch (error) {
        window.PH?.error?.('Promotions: no se pudo leer el carrito.', error);
        return null;
      }
    }

    normalizeBoolean(value) {
      if (value === true) return true;
      if (value === false) return false;

      const normalized = String(value ?? '').trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    isPackage(item) {
      if (!item) return false;

      const properties = item.properties || {};
      if (String(properties._ph_product_type || '').trim().toLowerCase() === 'package') {
        return true;
      }

      const identity = [item.product_title, item.title, item.handle, item.url]
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      return identity.includes('paquete');
    }

    isThreeForTwoEligible(item) {
      if (!item || this.isPackage(item)) return false;

      const properties = item.properties || {};
      const productType = String(properties._ph_product_type || 'perfume')
        .trim()
        .toLowerCase();

      if (productType !== 'perfume') return false;
      if (properties._ph_3x2_eligible === undefined) return true;

      return this.normalizeBoolean(properties._ph_3x2_eligible);
    }

    isVolumeEligible(item) {
      if (!item || this.isPackage(item)) return false;

      const properties = item.properties || {};

      if (
        properties._ph_volume_eligible === 'false' ||
        properties._ph_volume_eligible === false
      ) {
        return false;
      }

      /*
        Durante una campaña 3×2 activa, cualquier unidad marcada
        como elegible al 3×2 sale por completo del pool de volumen.
      */
      if (this.effectiveMode === '3x2' && this.isThreeForTwoEligible(item)) {
        return false;
      }

      return true;
    }

    getEligibleQuantity(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((total, item) => {
        if (!this.isVolumeEligible(item)) return total;
        return total + (Number(item.quantity) || 0);
      }, 0);
    }

    getProductRoot() {
      return document.querySelector('product-info') || document;
    }

    getSelectedQuantity() {
      const productRoot = this.getProductRoot();

      const input =
        productRoot.querySelector('quantity-input input[name="quantity"]') ||
        productRoot.querySelector('input[name="quantity"]');

      if (!input) return 1;

      const quantity = Number(input.value);

      if (!Number.isFinite(quantity) || quantity < 1) return 1;

      return quantity;
    }

    getSelectedVariantPromotion() {
      if (window.PHSelectedVariantPromotion) {
        return {
          variantId: window.PHSelectedVariantPromotion.variantId || null,
          variantTitle: window.PHSelectedVariantPromotion.variantTitle || '',
          productType: window.PHSelectedVariantPromotion.productType || 'perfume',
          variantType: window.PHSelectedVariantPromotion.variantType || 'regular',
          volumeEligible: window.PHSelectedVariantPromotion.volumeEligible !== false,
          threeForTwoEligible: Boolean(
            window.PHSelectedVariantPromotion.threeForTwoEligible
          )
        };
      }

      const productRoot = this.getProductRoot();
      const form = productRoot.querySelector('form[data-type="add-to-cart-form"]');

      if (!form) {
        return {
          variantId: null,
          variantTitle: '',
          productType: 'perfume',
          variantType: 'regular',
          volumeEligible: false,
          threeForTwoEligible: true
        };
      }

      const productType =
        form.querySelector('[data-ph-product-type]')?.value || 'perfume';
      const variantType =
        form.querySelector('[data-ph-variant-type]')?.value || 'regular';
      const volumeEligible = this.normalizeBoolean(
        form.querySelector('[data-ph-volume-eligible]')?.value ?? 'false'
      );
      const threeForTwoEligible = this.normalizeBoolean(
        form.querySelector('[data-ph-3x2-eligible]')?.value ?? 'true'
      );
      const variantId = form.querySelector('.product-variant-id')?.value || null;

      return {
        variantId,
        variantTitle: '',
        productType,
        variantType,
        volumeEligible,
        threeForTwoEligible
      };
    }

    resolveEngineMode(selectedVariantPromotion) {
      if (this.effectiveMode === 'none') return 'none';
      if (this.effectiveMode === 'volume') return 'volume';

      if (this.effectiveMode === '3x2') {
        if (selectedVariantPromotion?.productType === 'package') return 'none';
        if (selectedVariantPromotion?.threeForTwoEligible) return '3x2';
        return 'none';
      }

      return 'volume';
    }

    getVolumeState(quantity) {
      const tiers = PH_PROMOTIONS_CONFIG.volume.tiers;
      const activeTier = tiers.find((tier) => quantity >= tier.minimum);

      if (!activeTier) return { tier: 0, discount: 0 };

      return {
        tier: activeTier.minimum,
        discount: activeTier.discount
      };
    }

    getNextTierState(quantity) {
      if (quantity < 2) {
        return { nextTier: 2, nextDiscount: 10, remaining: 2 - quantity };
      }

      if (quantity < 3) {
        return { nextTier: 3, nextDiscount: 15, remaining: 3 - quantity };
      }

      if (quantity < 5) {
        return { nextTier: 5, nextDiscount: 20, remaining: 5 - quantity };
      }

      return { nextTier: 0, nextDiscount: 0, remaining: 0 };
    }

    getItemUnitPriceCents(item) {
      const candidates = [
        item?.original_price,
        item?.price,
        item?.final_price
      ];

      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value >= 0) return value;
      }

      return 0;
    }

    getThreeForTwoEligibleUnits(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];

      const units = [];

      cart.items.forEach((item) => {
        if (!this.isThreeForTwoEligible(item)) return;

        const quantity = Math.max(0, Number(item.quantity) || 0);
        const unitPriceCents = this.getItemUnitPriceCents(item);

        for (let index = 0; index < quantity; index += 1) {
          units.push({
            key: item.key || '',
            variantId: item.variant_id || item.id || null,
            productId: item.product_id || null,
            title: item.product_title || item.title || '',
            variantTitle: item.variant_title || '',
            quantityIndex: index,
            priceCents: unitPriceCents,
            url: item.url || '',
            image: item.image || null
          });
        }
      });

      return units;
    }

    formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;

      try {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    getThreeForTwoState(cart, selectedQuantity, selectedVariantPromotion) {
      const config = PH_PROMOTIONS_CONFIG.threeForTwo;
      const actualUnits = this.getThreeForTwoEligibleUnits(cart);
      const actualQuantity = actualUnits.length;

      const selectedEligible =
        this.effectiveMode === '3x2' &&
        selectedVariantPromotion?.productType !== 'package' &&
        selectedVariantPromotion?.threeForTwoEligible === true;

      const selectedThreeForTwoQuantity = selectedEligible ? selectedQuantity : 0;
      const projectedQuantity = actualQuantity + selectedThreeForTwoQuantity;

      const actualFreeItems = Math.floor(actualQuantity / config.groupSize);
      const projectedFreeItems = Math.floor(projectedQuantity / config.groupSize);

      const sortedUnits = [...actualUnits].sort((a, b) => {
        if (a.priceCents !== b.priceCents) return a.priceCents - b.priceCents;
        return String(a.title).localeCompare(String(b.title));
      });

      const freeUnits = sortedUnits.slice(0, actualFreeItems);
      const actualSavingsCents = freeUnits.reduce(
        (total, unit) => total + (Number(unit.priceCents) || 0),
        0
      );

      let remainingForNext = 3;

      if (projectedQuantity > 0) {
        const remainder = projectedQuantity % config.groupSize;
        remainingForNext = remainder === 0 ? 0 : config.groupSize - remainder;
      }

      return {
        actualQuantity,
        selectedQuantity: selectedThreeForTwoQuantity,
        projectedQuantity,
        actualFreeItems,
        projectedFreeItems,
        remainingForNext,
        actualSavingsCents,
        actualSavings: this.formatMoney(actualSavingsCents),
        cheapestItem: sortedUnits[0] || null,
        freeItems: freeUnits
      };
    }

    calculateState(cart) {
      this.refreshPromotionMode();

      const selectedQuantity = this.getSelectedQuantity();
      const selectedVariantPromotion = this.getSelectedVariantPromotion();

      this.engineMode = this.resolveEngineMode(selectedVariantPromotion);

      const actualQuantity = this.getEligibleQuantity(cart);
      const projectedQuantity = actualQuantity + selectedQuantity;

      const actualState = this.getVolumeState(actualQuantity);
      const selectedState = this.getVolumeState(selectedQuantity);

      /*
        Solo mostramos/descontamos volume en PDP cuando el motor actual
        de la variante seleccionada es volume.
      */
      /*
        La PDP comunica el precio unitario correspondiente a la cantidad que
        el cliente está seleccionando ahora. El carrito conserva su cálculo
        real por separado; no debe congelar el preview de la PDP en un nivel
        anterior cuando el usuario disminuye la cantidad.
      */
      const priceDiscount =
        this.engineMode === 'volume'
          ? selectedState.discount
          : 0;

      const visualQuantity = selectedQuantity;
      const visualState = this.getVolumeState(visualQuantity);
      const nextState = this.getNextTierState(visualQuantity);

      const threeForTwo = this.getThreeForTwoState(
        cart,
        selectedQuantity,
        selectedVariantPromotion
      );

      return {
        selectedMode: this.selectedMode,
        effectiveMode: this.effectiveMode,
        engineMode: this.engineMode,
        mode: this.engineMode,
        campaignStatus: this.getCampaignStatus(),
        threeForTwoStart: this.threeForTwoStart,
        threeForTwoEnd: this.threeForTwoEnd,

        currentVariantType: selectedVariantPromotion.variantType,
        currentVariant3x2Eligible: selectedVariantPromotion.threeForTwoEligible,
        currentProductType: selectedVariantPromotion.productType,

        actualQuantity,
        selectedQuantity,
        projectedQuantity,
        visualQuantity,

        tier: visualState.tier,
        discount: visualState.discount,
        actualDiscount: actualState.discount,
        actualTier: actualState.tier,
        selectedTier: selectedState.tier,
        selectedDiscount: selectedState.discount,
        priceDiscount,

        nextTier: nextState.nextTier,
        nextDiscount: nextState.nextDiscount,
        remainingForNextTier: nextState.remaining,

        threeForTwoActualQuantity: threeForTwo.actualQuantity,
        threeForTwoSelectedQuantity: threeForTwo.selectedQuantity,
        threeForTwoProjectedQuantity: threeForTwo.projectedQuantity,
        threeForTwoActualFreeItems: threeForTwo.actualFreeItems,
        threeForTwoProjectedFreeItems: threeForTwo.projectedFreeItems,
        threeForTwoRemainingForNext: threeForTwo.remainingForNext,
        threeForTwoActualSavingsCents: threeForTwo.actualSavingsCents,
        threeForTwoActualSavings: threeForTwo.actualSavings,
        threeForTwoCheapestItem: threeForTwo.cheapestItem,
        threeForTwoFreeItems: threeForTwo.freeItems
      };
    }

    getVolumeProgressMessage() {
      const visualQuantity = Number(this.state.visualQuantity) || 0;

      if (visualQuantity < 2) {
        return 'Lleva <strong>2 perfumes</strong> y obtén <strong>10% de descuento</strong>.';
      }

      if (visualQuantity === 2) {
        return 'Lleva <strong>3 perfumes</strong> y obtén <strong>15% de descuento</strong>.';
      }

      if (visualQuantity === 3) {
        return 'Llega a <strong>5 perfumes</strong> y obtén <strong>20% de descuento</strong>.';
      }

      if (visualQuantity === 4) {
        return 'Agrega <strong>1 perfume más</strong> y obtén <strong>20% de descuento</strong>.';
      }

      if (visualQuantity >= 5) {
        return 'Ya alcanzaste nuestro <strong>máximo ahorro: 20% de descuento</strong>.';
      }

      return '';
    }

    getThreeForTwoProgressMessage() {
      if (this.engineMode !== '3x2') return '';

      const projected = Number(this.state.threeForTwoProjectedQuantity) || 0;
      const remaining = Number(this.state.threeForTwoRemainingForNext) || 0;
      const projectedFree = Number(this.state.threeForTwoProjectedFreeItems) || 0;

      if (projected < 3) {
        const missing = Math.max(1, 3 - projected);
        return `Agrega <strong>${missing} perfume${missing === 1 ? '' : 's'} elegible${missing === 1 ? '' : 's'} más</strong> para completar tu <strong>3×2</strong>.`;
      }

      if (remaining === 0) {
        return `¡<strong>3×2 listo</strong>! Con esta selección llevas <strong>${projectedFree} gratis</strong> por cada grupo completado.`;
      }

      return `Tu <strong>3×2</strong> ya está activo. Agrega <strong>${remaining} perfume${remaining === 1 ? '' : 's'} más</strong> para completar otro grupo.`;
    }

    getProgressMessage() {
      if (this.engineMode === '3x2') return this.getThreeForTwoProgressMessage();
      if (this.engineMode === 'none') return '';
      return this.getVolumeProgressMessage();
    }

    getOrCreateProgressNode(table) {
      /*
        PH FIX:
        El nodo ya debe venir impreso por Liquid.
        Este fallback se conserva únicamente por seguridad
        para plantillas antiguas/caché.
      */
      let node = table.querySelector('[data-ph-volume-progress]');
      if (node) return node;

      node = document.createElement('p');
      node.className = 'ph-volume-discounts__progress';
      node.setAttribute('data-ph-volume-progress', '');
      node.setAttribute('aria-live', 'polite');

      const levels = table.querySelector('.ph-volume-discounts__levels');

      if (levels) {
        levels.after(node);
      } else {
        const note = table.querySelector('.ph-volume-discounts__note');
        if (note) note.before(node);
        else table.appendChild(node);
      }

      return node;
    }

    renderVolumeTable() {
      const tables = document.querySelectorAll('[data-ph-volume-discounts]');

      tables.forEach((table) => {
        /*
          Nunca dejamos visible la tabla 10/15/20 sobre una variante
          cuyo motor actual es 3×2 o none.
        */
        const showVolume = this.engineMode === 'volume';
        table.hidden = !showVolume;
        table.setAttribute('aria-hidden', showVolume ? 'false' : 'true');

        const levels = table.querySelectorAll('[data-ph-volume-level]');

        levels.forEach((level) => {
          const tier = Number(level.dataset.tier);
          const isActive = showVolume && tier === this.state.tier;

          level.classList.toggle('is-active', isActive);
          level.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        const progressNode = this.getOrCreateProgressNode(table);
        const progressMessage = showVolume ? this.getVolumeProgressMessage() : '';

        progressNode.innerHTML = progressMessage;
        progressNode.hidden = !progressMessage;

        table.dataset.activeTier = String(this.state.tier);
        table.dataset.activeDiscount = String(this.state.discount);
        table.dataset.priceDiscount = String(this.state.priceDiscount);
        table.dataset.actualQuantity = String(this.state.actualQuantity);
        table.dataset.selectedQuantity = String(this.state.selectedQuantity);
        table.dataset.projectedQuantity = String(this.state.projectedQuantity);
        table.dataset.visualQuantity = String(this.state.visualQuantity);
        table.dataset.nextTier = String(this.state.nextTier);
        table.dataset.nextDiscount = String(this.state.nextDiscount);

        table.dataset.selectedMode = String(this.state.selectedMode);
        table.dataset.effectiveMode = String(this.state.effectiveMode);
        table.dataset.engineMode = String(this.state.engineMode);
        table.dataset.campaignStatus = String(this.state.campaignStatus);
      });
    }

    renderPromotionStateToDOM() {
      const root = this.getProductRoot();
      if (!root || root === document) return;

      root.dataset.phSelectedMode = String(this.state.selectedMode);
      root.dataset.phEffectiveMode = String(this.state.effectiveMode);
      root.dataset.phEngineMode = String(this.state.engineMode);
      root.dataset.phCampaignStatus = String(this.state.campaignStatus);
      root.dataset.phCurrentVariantType = String(this.state.currentVariantType || '');
      root.dataset.phCurrentVariant3x2Eligible = String(
        Boolean(this.state.currentVariant3x2Eligible)
      );
      root.dataset.ph3x2ActualQuantity = String(this.state.threeForTwoActualQuantity);
      root.dataset.ph3x2ProjectedQuantity = String(this.state.threeForTwoProjectedQuantity);
      root.dataset.ph3x2ActualFreeItems = String(this.state.threeForTwoActualFreeItems);
      root.dataset.ph3x2ProjectedFreeItems = String(this.state.threeForTwoProjectedFreeItems);
      root.dataset.ph3x2RemainingForNext = String(this.state.threeForTwoRemainingForNext);
    }

    dispatchUpdate() {
      document.dispatchEvent(
        new CustomEvent('ph:promotion:update', {
          detail: {
            ...this.state,
            message: this.getProgressMessage(),
            volumeMessage: this.getVolumeProgressMessage(),
            threeForTwoMessage: this.getThreeForTwoProgressMessage(),
            cart: this.cart
          }
        })
      );
    }

    renderCurrentState() {
      if (!this.cart) return;

      this.state = this.calculateState(this.cart);
      this.renderVolumeTable();
      this.renderPromotionStateToDOM();
      this.dispatchUpdate();

      window.PH?.log?.('Promotions:', this.state);
    }

    async refresh() {
      const cart = await this.getCart();
      if (!cart) return;

      this.cart = cart;
      this.state = this.calculateState(cart);

      this.renderVolumeTable();
      this.renderPromotionStateToDOM();
      this.dispatchUpdate();

      window.PH?.log?.('Promotions:', this.state);
    }

    scheduleRefresh(delay = 120) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), delay);
    }

    handleQuantityChange() {
      window.clearTimeout(this.quantityTimer);
      this.quantityTimer = window.setTimeout(() => this.renderCurrentState(), 20);
    }

    bindQuantitySelector() {
      const productRoot = this.getProductRoot();

      productRoot.addEventListener('input', (event) => {
        const target = event.target;
        if (!target?.matches?.('input[name="quantity"]')) return;
        this.handleQuantityChange();
      });

      productRoot.addEventListener('change', (event) => {
        const target = event.target;
        if (!target?.matches?.('input[name="quantity"]')) return;
        this.handleQuantityChange();
      });

      productRoot.addEventListener('click', (event) => {
        const button = event.target.closest('quantity-input .quantity__button');
        if (!button) return;

        window.setTimeout(() => this.handleQuantityChange(), 35);
      });
    }

    bindVariantPromotionUpdates() {
      document.addEventListener('ph:variant-promotion:update', () => {
        /*
          El snippet buy-buttons actualiza primero
          window.PHSelectedVariantPromotion; luego recalculamos.
        */
        window.setTimeout(() => this.renderCurrentState(), 0);
        window.setTimeout(() => this.renderCurrentState(), 90);
      });
    }

    bindCartUpdates() {
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleRefresh(180));
      }

      ['cart:updated', 'cart:refresh', 'ph:cart:changed'].forEach((eventName) => {
        document.addEventListener(eventName, () => this.scheduleRefresh(180));
      });

      /*
       * PH FIX: se quitó el parche global de window.fetch / XMLHttpRequest.
       * Era redundante (el tema ya publica PUB_SUB_EVENTS.cartUpdate en cada
       * agregado exitoso, ya escuchado arriba) y al sobreescribir fetch/XHR
       * en TODA la página de producto podía interferir con otras apps que
       * también parchan window.fetch para detectar cambios de carrito —
       * como el Smart Cart de Rebuy — impidiendo que tomen el control del
       * flujo y dejando que se abra el carrito nativo en su lugar.
       */

      window.addEventListener('pageshow', () => {
        this.refreshPromotionMode();
        this.scheduleRefresh(100);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.refreshPromotionMode();
          this.scheduleRefresh(100);
        }
      });
    }
  }

  const startPHPromotions = () => {
    if (window.PHPromotions) return;

    const promotions = new PHPromotions();
    promotions.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPHPromotions, { once: true });
  } else {
    startPHPromotions();
  }
})();
